/**
 * Tiny native-vs-web wrapper around @capacitor/dialog. On native, surfaces
 * the OS-native UIAlertController / AlertDialog — looks trustworthy and
 * unmistakable for irreversible actions. On web, falls back to the standard
 * window.alert / confirm / prompt so callers don't have to branch.
 *
 * For routine non-destructive UI prompts, prefer the in-app React modals —
 * native dialogs interrupt the user's flow and shouldn't be used for
 * every "are you sure".
 */

import { isNativePlatform } from '@/lib/platform';

interface ConfirmOptions {
    title?: string;
    message: string;
    okButtonTitle?: string;
    cancelButtonTitle?: string;
}

interface AlertOptions {
    title?: string;
    message: string;
    buttonTitle?: string;
}

interface PromptOptions {
    title?: string;
    message: string;
    inputPlaceholder?: string;
    inputText?: string;
    okButtonTitle?: string;
    cancelButtonTitle?: string;
}

interface PromptResult {
    value: string;
    cancelled: boolean;
}

export async function nativeConfirm(opts: ConfirmOptions): Promise<boolean> {
    if (isNativePlatform()) {
        try {
            const { Dialog } = await import('@capacitor/dialog');
            const { value } = await Dialog.confirm({
                title: opts.title,
                message: opts.message,
                okButtonTitle: opts.okButtonTitle,
                cancelButtonTitle: opts.cancelButtonTitle,
            });
            return value;
        } catch {
            // Fall through to window.confirm so the user still gets *some* prompt.
        }
    }
    if (typeof window === 'undefined') return false;
    const prefix = opts.title ? `${opts.title}\n\n` : '';
    return window.confirm(`${prefix}${opts.message}`);
}

export async function nativeAlert(opts: AlertOptions): Promise<void> {
    if (isNativePlatform()) {
        try {
            const { Dialog } = await import('@capacitor/dialog');
            await Dialog.alert({
                title: opts.title,
                message: opts.message,
                buttonTitle: opts.buttonTitle,
            });
            return;
        } catch {
            /* fall through */
        }
    }
    if (typeof window === 'undefined') return;
    const prefix = opts.title ? `${opts.title}\n\n` : '';
    window.alert(`${prefix}${opts.message}`);
}

export async function nativePrompt(opts: PromptOptions): Promise<PromptResult> {
    if (isNativePlatform()) {
        try {
            const { Dialog } = await import('@capacitor/dialog');
            const result = await Dialog.prompt({
                title: opts.title,
                message: opts.message,
                inputPlaceholder: opts.inputPlaceholder,
                inputText: opts.inputText,
                okButtonTitle: opts.okButtonTitle,
                cancelButtonTitle: opts.cancelButtonTitle,
            });
            return { value: result.value, cancelled: result.cancelled };
        } catch {
            /* fall through */
        }
    }
    if (typeof window === 'undefined') return { value: '', cancelled: true };
    const prefix = opts.title ? `${opts.title}\n\n` : '';
    const value = window.prompt(`${prefix}${opts.message}`, opts.inputText ?? '');
    if (value == null) return { value: '', cancelled: true };
    return { value, cancelled: false };
}
